/**
 * Glyphes de l'aside — namespace `nav:` (DESIGN.md §6, exception bornée).
 *
 * Grammaire : un glyphe en APLAT sur une grille 24, posé dans une pastille que
 * dessine la feuille de style (`_nav-icons.scss`), jamais le SVG. Un seul détail
 * porte l'accent, comme la barre haute du symbole (§14.2). Aucune couleur ici :
 * encre, accent, pastille et état actif sont des variables CSS.
 *
 * Classes de chemin :
 * - `i`  encre, aplat (`fill-rule: evenodd` : les sous-chemins intérieurs sont des creux)
 * - `x`  accent, aplat
 * - `is` / `xs`  encre / accent au trait, pour les rares formes qui ne tiennent pas
 *   en aplat (toit, lien, coche, clé)
 * - `it` encre au trait fin, pour un symbole posé sur l'accent (l'euro)
 *
 * SVG inline plutôt que fichiers : l'aside se dessine au premier rendu, sans
 * aller-retour réseau par icône comme le namespace `mat:` (jsDelivr).
 */

const path = (cls: string, d: string): string => `<path class="${cls}" d="${d}"/>`;
const circle = (cls: string, cx: number, cy: number, r: number): string =>
  `<circle class="${cls}" cx="${cx}" cy="${cy}" r="${r}"/>`;
const rect = (cls: string, x: number, y: number, width: number, height: number, rx: number): string =>
  `<rect class="${cls}" x="${x}" y="${y}" width="${width}" height="${height}" rx="${rx}"/>`;

const HOUSE = 'M2.6 10.8L12 3.3l9.4 7.5-.9 1.2-1-.8V20a1 1 0 0 1-1 1H6.5a1 1 0 0 1-1-1v-8.8l-1 .8z';
// Pastille « + » d'un ajout, avec la croix en creux.
const PLUS_BADGE =
  'M13.3 16.7a4.2 4.2 0 1 0 8.4 0a4.2 4.2 0 1 0-8.4 0z' +
  'M16.8 14h1.4v2h2v1.4h-2v2h-1.4v-2h-2V16h2z';

const glyphs = {
  // Espace propriétaire / gestionnaire
  rocket:
    path('i', 'M12 2.5c3.6 2.6 5.3 6.4 5.3 10.8V16H6.7v-2.7C6.7 8.9 8.4 5.1 12 2.5zM12 7.6a1.8 1.8 0 1 0 0 3.6a1.8 1.8 0 1 0 0-3.6z') +
    path('i', 'M6.7 12.5L3.5 15.8V19l3.2-1.5zM17.3 12.5l3.2 3.3V19l-3.2-1.5z') +
    path('x', 'M9.3 17.3h5.4L12 22z'),
  cockpit:
    path('i', 'M3 17a9 9 0 0 1 18 0h-3.6a5.4 5.4 0 0 0-10.8 0z') +
    path('x', 'M11 17.6l5.4-7.4 1.3 1-4.6 7.8a1.3 1.3 0 0 1-2.1-1.4z'),
  spark:
    path('x', 'M11 2.5l2.2 6.3 6.3 2.2-6.3 2.2L11 19.5l-2.2-6.3L2.5 11l6.3-2.2z') +
    path('i', 'M18.5 14.5l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1z'),
  inbox:
    path('i', 'M3 10.3L12 3.8l9 6.5V19a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 19z') +
    path('x', 'M6.5 11.8l5.5 3.8 5.5-3.8v2.4L12 18l-5.5-3.8z'),
  toolbox:
    path('i', 'M3 9.5h18V19a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 19z') +
    path('i', 'M8.2 9.5V6.8A1.8 1.8 0 0 1 10 5h4a1.8 1.8 0 0 1 1.8 1.8v2.7H14V7h-4v2.5z') +
    rect('x', 9.8, 11.8, 4.4, 4.2, 1),
  finance:
    rect('i', 3.5, 14, 4, 7, 2) +
    rect('i', 10, 11, 4, 10, 2) +
    rect('i', 16.5, 14.5, 4, 6.5, 2) +
    path('xs', 'M3.5 9.5l5-4.2 4 2.7 7-4.5') +
    path('xs', 'M16.2 3.3h3.3v3.2'),
  'rent-arrears':
    path('i', 'M5 4.5h14a2 2 0 0 1 2 2V11a6 6 0 0 0-8.7 9.5H5a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2zM5 8h14v1.2H5z') +
    rect('i', 7, 2.5, 2, 3.5, 1) +
    rect('i', 15, 2.5, 2, 3.5, 1) +
    path('x', 'M13 16.5a4.2 4.2 0 1 0 8.4 0a4.2 4.2 0 1 0-8.4 0zM16.5 14h1.4v3.2h-1.4zM16.5 18.1h1.4v1.4h-1.4z'),
  house:
    path('i', HOUSE) +
    path('x', 'M10 21v-4.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1V21z'),
  tenant:
    path('i', 'M2.5 10.5L12 3.4l9.5 7.1-1.2 1.6L12 6 3.7 12.1z') +
    circle('x', 12, 11, 2.5) +
    path('x', 'M7 21a5 5 0 0 1 10 0z'),
  lease:
    path('i', 'M6 2.5h8l5 5V20a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V4A1.5 1.5 0 0 1 6 2.5zM8 8.5h5V10H8zM8 12h8v1.5H8z') +
    circle('x', 15.8, 17.5, 2.8),
  calculator:
    path('i', 'M7 2.5h10a2 2 0 0 1 2 2v15a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-15a2 2 0 0 1 2-2zM8 5.5h8v3H8z') +
    path('x', 'M8 11.5h2.2v2.2H8zM10.9 11.5h2.2v2.2h-2.2zM13.8 11.5H16v2.2h-2.2zM8 15.5h2.2v2.2H8zM10.9 15.5h2.2v2.2h-2.2zM13.8 15.5H16v2.2h-2.2z'),
  // Sous-entrées d'« Outils » : visibles au rail seulement.
  loan:
    path('i', 'M2.5 9L12 3.5 21.5 9v1.5h-19z') +
    rect('i', 5, 12, 2.4, 6, 0.6) +
    rect('i', 10.8, 12, 2.4, 6, 0.6) +
    rect('i', 16.6, 12, 2.4, 6, 0.6) +
    rect('x', 3, 19, 18, 2, 1),
  investment:
    rect('i', 3, 16.6, 9, 2.4, 1.2) +
    rect('i', 3, 13.2, 9, 2.4, 1.2) +
    rect('i', 3, 9.8, 9, 2.4, 1.2) +
    path('xs', 'M13.5 16.5l6-6') +
    path('xs', 'M15.5 10.5h4v4'),
  indexation:
    circle('i', 7.2, 7.2, 3.2) +
    circle('i', 16.8, 16.8, 3.2) +
    path('xs', 'M18.5 4.5L5.5 19.5'),
  // Simulateur de loyer : une règle graduée.
  rent:
    rect('i', 3, 8, 18, 8, 1.5) +
    path('xs', 'M7 8v3.5M11 8v2.5M15 8v3.5M19 8v2.5'),
  provider:
    path('i', 'M5 21a7 7 0 0 1 14 0z') +
    circle('i', 12, 12.3, 3.2) +
    path('x', 'M7 9.3a5 5 0 0 1 10 0zM5 9.3h14v1.6H5z'),
  folder:
    path('i', 'M3 6.5A1.5 1.5 0 0 1 4.5 5h5l2 2.2h8A1.5 1.5 0 0 1 21 8.7v9.8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z') +
    path('x', 'M3 6.5A1.5 1.5 0 0 1 4.5 5h5l2 2.2H3z') +
    rect('x', 6, 11, 5, 1.6, 0.8),

  // Espace syndic
  building:
    path('i', 'M5 21V8.5l7-5 7 5V21zM8.8 10h2.2v2.2H8.8zM13 10h2.2v2.2H13zM8.8 14h2.2v2.2H8.8zM13 14h2.2v2.2H13z') +
    path('x', 'M10.5 21v-3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v3z'),
  'building-plus':
    path('i', 'M3 21V9l6-4.5L15 9v2.4a6 6 0 0 0-2.6 9.6zM6.2 10.5h1.8v1.8H6.2zM10 10.5h1.8v1.8H10zM6.2 14h1.8v1.8H6.2z') +
    path('x', PLUS_BADGE),
  link:
    path('is', 'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1') +
    path('xs', 'M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1'),
  wrench:
    path('i', 'M15.5 3a5 5 0 0 0-4.7 6.8L3.6 17a2 2 0 0 0 2.8 2.8l7.2-7.2A5 5 0 0 0 20.4 8l-3 3-2.7-.7-.7-2.7 3-3A5 5 0 0 0 15.5 3z') +
    circle('x', 5, 18.4, 1.2),

  // Espace agence
  dashboard:
    path('is', 'M3 10L12 3.5 21 10') +
    rect('i', 5, 12, 6, 4, 1) +
    rect('i', 5, 17.5, 6, 3.5, 1) +
    rect('i', 13, 17.5, 6, 3.5, 1) +
    rect('x', 13, 12, 6, 4, 1),
  sunrise:
    rect('i', 2, 18.5, 20, 2, 1) +
    path('x', 'M6 17.5a6 6 0 0 1 12 0z') +
    path('is', 'M12 4v2.5M4.8 8.2l1.8 1.6M19.2 8.2l-1.8 1.6'),
  lead:
    circle('i', 10, 7.8, 3.5) +
    path('i', 'M3 20.5a7 7 0 0 1 11.6-5.3 6 6 0 0 0-1 5.3z') +
    path('x', PLUS_BADGE),
  valuation:
    path('i', HOUSE) +
    circle('x', 12, 15.4, 3.9) +
    path('it', 'M13.5 13.9a1.9 1.9 0 1 0 0 3M10 14.9h2.4M10 16h2.4'),
  viewing:
    path('i', HOUSE) +
    path('x', 'M6.2 15.5s2.3-3.4 5.8-3.4 5.8 3.4 5.8 3.4-2.3 3.4-5.8 3.4-5.8-3.4-5.8-3.4z') +
    circle('i', 12, 15.5, 1.5),
  mandate:
    path('i', 'M6.5 4.5h11A1.5 1.5 0 0 1 19 6v14a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V6a1.5 1.5 0 0 1 1.5-1.5z') +
    rect('x', 8.8, 2.5, 6.4, 3.8, 1) +
    path('xs', 'M8.8 13.5l2.2 2.2 4.2-4.2'),
  deal:
    path('i', HOUSE) +
    path('xs', 'M9.3 16.3a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0-4.4 0M13.7 16.3h3.6M15.8 16.3v1.8'),
  podium:
    rect('i', 4, 14, 4, 7, 2) +
    rect('i', 10, 10.5, 4, 10.5, 2) +
    rect('x', 16, 7, 4, 14, 2) +
    circle('i', 6, 10.8, 1.9) +
    circle('i', 12, 7.3, 1.9) +
    circle('x', 18, 3.8, 1.9),
  plug:
    path('i', 'M6.5 7.5h11V11a5.5 5.5 0 0 1-11 0z') +
    rect('i', 8.2, 2.5, 2, 5.5, 1) +
    rect('i', 13.8, 2.5, 2, 5.5, 1) +
    rect('x', 11, 15.5, 2, 6, 1),
  website:
    path('i', 'M5 3.5h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2zM5 8.5v10h14v-10z') +
    path('x', 'M8 17.5v-3.6l4-3.4 4 3.4v3.6z'),
  sliders:
    rect('i', 3, 6, 18, 2, 1) +
    rect('i', 3, 16, 18, 2, 1) +
    circle('x', 15, 7, 3) +
    circle('i', 9, 17, 3),

  // Espace prestataire
  bell:
    path('i', 'M12 3a6 6 0 0 1 6 6v5l2 3H4l2-3V9a6 6 0 0 1 6-6z') +
    path('i', 'M9.5 18.5h5a2.5 2.5 0 0 1-5 0z') +
    circle('x', 18.3, 5, 3),
  profile:
    path('i', 'M5 4.5h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2zM14 9h4v1.6h-4zM14 12.5h4v1.6h-4z') +
    circle('x', 8.5, 9.8, 2.2) +
    path('x', 'M5 16.8a3.5 3.5 0 0 1 7 0z')
} satisfies Record<string, string>;

export type NavIconName = keyof typeof glyphs;

export const NAV_ICONS: Readonly<Record<NavIconName, string>> = glyphs;

/** `svgIcon` d'une entrée de l'aside : un nom absent du jeu ne compile pas. */
export function navIcon(name: NavIconName): `nav:${NavIconName}` {
  return `nav:${name}`;
}

/** Le SVG complet enregistré dans `MatIconRegistry` — sans pastille, dessinée en CSS. */
export function navIconSvg(name: NavIconName): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="nav-icon">${NAV_ICONS[name]}</svg>`;
}
